import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

/**
 * The codepoints attackers actually hide inside documents. Using them as the
 * ambient texture means the background states the product's premise: these
 * characters are there, you just cannot see them until something scans.
 */
const GLYPHS = [
  "U+200B", "U+200C", "U+200D", "U+FEFF", "U+2060", "U+00AD",
  "ZWSP", "ZWJ", "ZWNJ", "BOM", "WJ", "SHY", "U+180E", "U+2062",
];

const REVEAL_RADIUS = 200;
const MAX_GLYPHS = 68;

/**
 * Five-layer decorative backdrop: aurora mesh, blueprint grid, a canvas glyph
 * field that reveals itself around the pointer, a pointer spotlight, and
 * grain/vignette on top.
 *
 * Entirely inert (`aria-hidden`, `pointer-events: none`) and it degrades to a
 * single static frame under `prefers-reduced-motion`.
 */
function Backdrop() {
  const canvasRef = useRef(null);
  const spotlightRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Pointer position shared by the canvas field and the CSS spotlight.
  const pointer = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const spotlight = spotlightRef.current;
    let frame = 0;

    const onMove = (event) => {
      pointer.current.x = event.clientX;
      pointer.current.y = event.clientY;
      pointer.current.active = true;

      // Writing CSS variables is throttled to one write per frame; doing it on
      // every pointermove would thrash style recalculation.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!spotlight) return;
        const px = (event.clientX / window.innerWidth) * 100;
        const py = (event.clientY / window.innerHeight) * 100;
        spotlight.style.setProperty("--mx", `${px}%`);
        spotlight.style.setProperty("--my", `${py}%`);
      });
    };

    const onLeave = () => {
      pointer.current.active = false;
      pointer.current.x = -9999;
      pointer.current.y = -9999;
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    window.addEventListener("blur", onLeave);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let raf = 0;
    let particles = [];
    let width = 0;
    let height = 0;
    let running = true;

    const build = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Density scales with viewport area, capped so the per-frame text draw
      // cost stays well inside the 16ms budget on modest hardware.
      const target = Math.min(MAX_GLYPHS, Math.max(16, Math.round((width * height) / 26000)));
      particles = Array.from({ length: target }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.16,
        vy: (Math.random() - 0.5) * 0.16 - 0.05,
        size: 9 + Math.random() * 4,
        base: 0.05 + Math.random() * 0.06,
        glyph: GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
      }));
    };

    const paint = (animate) => {
      ctx.clearRect(0, 0, width, height);
      const { x: px, y: py, active } = pointer.current;

      for (const p of particles) {
        if (animate) {
          p.x += p.vx;
          p.y += p.vy;
          // Wrap with a margin so glyphs never pop at the edges.
          if (p.x < -60) p.x = width + 60;
          if (p.x > width + 60) p.x = -60;
          if (p.y < -40) p.y = height + 40;
          if (p.y > height + 40) p.y = -40;
        }

        let reveal = 0;
        if (active) {
          const dx = p.x - px;
          const dy = p.y - py;
          const dist = Math.hypot(dx, dy);
          if (dist < REVEAL_RADIUS) reveal = 1 - dist / REVEAL_RADIUS;
        }

        ctx.font = `${p.size}px "JetBrains Mono", ui-monospace, monospace`;
        ctx.textBaseline = "middle";

        if (reveal > 0.02) {
          // Inside the scan radius the hidden character fluoresces and picks
          // up a detection bracket — the same language as a positive result.
          const alpha = p.base + reveal * 0.85;
          ctx.fillStyle = `rgba(255, 95, 86, ${alpha.toFixed(3)})`;
          ctx.fillText(p.glyph, p.x, p.y);

          const w = ctx.measureText(p.glyph).width;
          ctx.strokeStyle = `rgba(255, 95, 86, ${(reveal * 0.5).toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.strokeRect(p.x - 3, p.y - p.size * 0.75, w + 6, p.size * 1.5);
        } else {
          ctx.fillStyle = `rgba(148, 170, 196, ${p.base.toFixed(3)})`;
          ctx.fillText(p.glyph, p.x, p.y);
        }
      }
    };

    const loop = () => {
      if (!running) return;
      paint(true);
      raf = requestAnimationFrame(loop);
    };

    build();

    if (prefersReducedMotion) {
      // One static frame: the texture survives, the motion does not.
      paint(false);
    } else {
      loop();
    }

    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        build();
        if (prefersReducedMotion) paint(false);
      }, 180);
    };

    // Stop burning frames while the tab is in the background.
    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      } else if (!prefersReducedMotion && !running) {
        running = true;
        loop();
      }
    };

    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [prefersReducedMotion]);

  return (
    <div className="backdrop" aria-hidden="true">
      <div className="backdrop__aurora">
        <span className="backdrop__blob backdrop__blob--a" />
        <span className="backdrop__blob backdrop__blob--b" />
        <span className="backdrop__blob backdrop__blob--c" />
      </div>
      <div className="backdrop__grid" />
      <canvas ref={canvasRef} className="backdrop__canvas" />
      <div className="backdrop__scan" />
      <div ref={spotlightRef} className="backdrop__spotlight" />
      <div className="backdrop__grain" />
      <div className="backdrop__vignette" />
    </div>
  );
}

export default Backdrop;
